import { memo, useCallback, useContext, useEffect, useState } from "react"
import styled from "styled-components"
import { useDispatch } from "react-redux"
import { useLocale, useT } from "react-polyglot-hooks"
import * as PropTypes from "prop-types"
import dynamic from "next/dynamic"
import isEqual from "lodash/isEqual"
import { emptyCarsFilter, updateCarsFilter } from "redux/actions"
import { sendEvent } from "scripts/GoogleAnalytics"
import PriceSelector from "components/forms/inputs/PriceSelector"
import FuelSelector from "components/forms/inputs/FuelSelector"
import useFilter from "hooks/useFilter"
import FilterContextProvider from "scenes/RentingScene/FilterContext"
import { RentingContext } from "scenes/RentingScene/RentingContext"
import Typography from "components/styled/Typography"
import KindSelector from "components/forms/inputs/KindSelector"
import BrandSelector from "components/forms/inputs/BrandSelector"
import TransmissionSelector from "components/forms/inputs/TransmissionSelector"
import TagsSelector from "components/forms/inputs/TagsSelector"
import Icon from "components/icons/icon"
import Trash from "components/icons/trash"
import { Close } from "components/icons/close"

const DurationSelector = dynamic(() => import("components/forms/inputs/DurationSelector"))
const JobSelector = dynamic(() => import("components/forms/inputs/JobSelector"))
const ProvinceSelector = dynamic(() => import("components/forms/inputs/ProvinceSelector"))

const FormBlock = styled.div`
  margin-bottom: 1.5rem;

  > label,
  > * > label {
    font-weight: bold;

    &:only-child {
      display: none;
    }
  }
`

const ClearButton = styled.button`
  background: none;
  border: none;
  margin: 0 auto;
  display: flex;
  vertical-align: middle;
  margin-top: 25px;
`

const TrashIcon = styled(Icon)`
  margin-right: 10px;
`

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
`

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.deepPink};
  font-size: 12px;
  line-height: 18px;
  svg {
    margin-left: 5px;
    font-size: 16px;
  }
`
function Filter({ handleClose, page, onUpdate }) {
  const t = useT()
  const locale = useLocale()
  const dispatch = useDispatch()
  const { carsFilter } = useFilter()
  const [filter, setFilter] = useState(carsFilter)
  const { variant } = useContext(RentingContext)

  useEffect(() => {
    setFilter(carsFilter)
  }, [carsFilter])

  useEffect(() => {
    if (!isEqual(filter, carsFilter)) {
      handleSubmit()
      onUpdate()
    }
  }, [filter])

  const handleSubmit = () => {
    dispatch(updateCarsFilter(filter))
  }

  const clearFilters = () => {
    dispatch(emptyCarsFilter())
    sendEvent("Filter delete", { event_category: page, event_label: "delete" })
  }

  const onChange = useCallback(
    (field, val) => {
      const value = Array.isArray(val) ? val.sort() : val
      if (filter[field] !== value) {
        const data = {}
        data[field] = value
        setFilter({ ...filter, ...data })
        sendEvent(`Filter ${field}`, { event_category: page, event_label: JSON.stringify(value) })
      }
    },
    [filter, page]
  )

  return (
    <FilterContextProvider value={filter}>
      <FormBlock>
        <Top>
          <Typography variant="title4" weight="medium">
            {t("renting.filter.title")}
          </Typography>
          <CloseButton onClick={handleClose}>
            {t("renting.filter.close")}
            <Icon onClick={handleClose} Icon={Close} size="xs" />
          </CloseButton>
        </Top>
        <Typography variant="small" color="gray">
          {t("renting.filter.subtitle")}
        </Typography>
      </FormBlock>
      {locale !== "es" && (
        <FormBlock>
          <JobSelector
            field="job_type"
            label={t("renting.filter.job_type")}
            handleChange={onChange}
            value={filter.job_type}
            filter
            multiple
          />
        </FormBlock>
      )}
      <FormBlock>
        <KindSelector
          field="kind"
          label={t("renting.filter.kind")}
          handleChange={onChange}
          value={filter.kind}
          filter
          multiple
        />
      </FormBlock>
      <FormBlock>
        <BrandSelector
          field="brand"
          label={t("renting.filter.brand")}
          handleChange={onChange}
          value={filter.brand}
          filter
        />
      </FormBlock>
      <FormBlock>
        <FuelSelector field="fuel" label={t("renting.filter.fuel")} handleChange={onChange} value={filter.fuel} />
      </FormBlock>
      <FormBlock>
        <PriceSelector
          field="priceRanges"
          label={t("renting.filter.price")}
          handleChange={onChange}
          value={filter.priceRanges}
        />
      </FormBlock>
      {variant === "stock" && (
        <FormBlock>
          <DurationSelector
            field="duration"
            label={t("renting.filter.duration")}
            handleChange={onChange}
            value={filter.duration}
          />
        </FormBlock>
      )}
      <FormBlock>
        <TransmissionSelector
          field="transmission"
          label={t("renting.filter.transmission")}
          handleChange={onChange}
          value={filter.transmission}
        />
      </FormBlock>
      <FormBlock>
        <TagsSelector field="tags" label={t("renting.filter.tags")} handleChange={onChange} value={filter.tags} />
      </FormBlock>
      {locale === "es" && page === "pre-owned" && (
        <FormBlock>
          <ProvinceSelector
            field="province"
            label="Ubicación del coche"
            handleChange={onChange}
            value={filter.province}
            filter
            multiple
          />
        </FormBlock>
      )}
      <FormBlock>
        <ClearButton onClick={clearFilters}>
          <TrashIcon color="deepPink" Icon={Trash} size="sm" />
          <Typography variant="text3" color="deepPink">
            {t("renting.filter.clear")}
          </Typography>
        </ClearButton>
      </FormBlock>
    </FilterContextProvider>
  )
}

Filter.propTypes = {
  handleClose: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  page: PropTypes.string.isRequired
}

export default memo(Filter)
